import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Chip,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Divider,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsightsIcon from '@mui/icons-material/Insights';
import { API_URL } from '../../api';
import EmailAttachmentsInput from '../../components/EmailAttachmentsInput';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const RESP_COLOR = { yes: 'success', no: 'error', maybe: 'warning' };
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

export default function RenewalsPage() {
    const confirm = useConfirm();
    const [surveys, setSurveys] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState('');
    const [academicYearLabel, setAcademicYearLabel] = useState('');
    const [defaultMessage, setDefaultMessage] = useState('');
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [perClassOverrides, setPerClassOverrides] = useState({});

    const [openSurvey, setOpenSurvey] = useState(null);
    const [responses, setResponses] = useState([]);
    const [counts, setCounts] = useState({});

    // Email preview dialog
    const [previewBusy, setPreviewBusy] = useState(false);
    const [previewState, setPreviewState] = useState(null);
    // { surveyId, surveyName, classOptions: [{_id, label}], classId, followUp,
    //   subject, html, classLabel }

    // Renewal intent dialog
    const [intentOpen, setIntentOpen] = useState(false);
    const [intentLabel, setIntentLabel] = useState('');
    const [intentData, setIntentData] = useState(null); // { rows, totals, label, surveys }
    const [intentBusy, setIntentBusy] = useState(false);
    const [intentFilter, setIntentFilter] = useState('all'); // all | yes | maybe | no | unanswered
    const [intentGroupBy, setIntentGroupBy] = useState('class'); // class | yearSubject

    const load = async () => {
        setLoading(true);
        try {
            const [s, c] = await Promise.all([
                axios.get(`${API_URL}/api/admin/renewal-surveys`, authHeaders()),
                axios.get(`${API_URL}/api/admin/tutoring-classes?status=published`, authHeaders()),
            ]);
            setSurveys(s.data || []);
            setClasses(c.data || []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setName('');
        setAcademicYearLabel('');
        setDefaultMessage('');
        setSelectedClasses([]);
        setPerClassOverrides({});
        setDialogOpen(true);
    };

    const openEdit = (s) => {
        setEditing(s);
        setName(s.name || '');
        setAcademicYearLabel(s.academicYearLabel || '');
        setDefaultMessage(s.defaultMessage || '');
        setSelectedClasses((s.tutoringClassIds || []).map((c) => c._id || c));
        const overrides = {};
        for (const m of s.perClassMessages || []) {
            overrides[String(m.tutoringClassId)] = m.message;
        }
        setPerClassOverrides(overrides);
        setDialogOpen(true);
    };

    const save = async () => {
        try {
            const payload = {
                name: name.trim(),
                academicYearLabel: academicYearLabel.trim(),
                defaultMessage,
                tutoringClassIds: selectedClasses,
                perClassMessages: Object.entries(perClassOverrides)
                    .filter(([, v]) => v && v.trim())
                    .map(([k, v]) => ({ tutoringClassId: k, message: v })),
            };
            if (editing) {
                await axios.patch(`${API_URL}/api/admin/renewal-surveys/${editing._id}`, payload, authHeaders());
            } else {
                await axios.post(`${API_URL}/api/admin/renewal-surveys`, payload, authHeaders());
            }
            setDialogOpen(false);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete survey', message: 'Delete this renewal survey?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/renewal-surveys/${id}`, authHeaders());
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    // Send / Follow-up flows: open a small dialog so the admin can attach
    // files alongside the survey email. `kind` is 'send' or 'followUp'.
    const [sendDialog, setSendDialog] = useState(null); // { kind, surveyId, surveyName }
    const [sendAttachments, setSendAttachments] = useState([]);
    const [sendBusy, setSendBusy] = useState(false);

    const openSendDialog = (kind, survey) => {
        setSendDialog({ kind, surveyId: survey._id, surveyName: survey.name });
        setSendAttachments([]);
    };
    const submitSendDialog = async () => {
        if (!sendDialog) return;
        setSendBusy(true);
        try {
            if (sendDialog.kind === 'send') {
                const { data } = await axios.post(
                    `${API_URL}/api/admin/renewal-surveys/${sendDialog.surveyId}/send`,
                    { attachments: sendAttachments },
                    authHeaders()
                );
                setMessage(`Sent — ${data.mailed} emails went out (${data.created} new rows).`);
                load();
            } else {
                const { data } = await axios.post(
                    `${API_URL}/api/admin/renewal-surveys/${sendDialog.surveyId}/follow-up`,
                    { attachments: sendAttachments },
                    authHeaders()
                );
                setMessage(`Follow-up emails sent: ${data.mailed} of ${data.candidates}.`);
            }
            setSendDialog(null);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setSendBusy(false);
        }
    };

    const send = (id) => {
        const s = surveys.find((x) => x._id === id);
        if (s) openSendDialog('send', s);
    };
    const followUp = (id) => {
        const s = surveys.find((x) => x._id === id);
        if (s) openSendDialog('followUp', s);
    };

    const openResponses = async (s) => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/renewal-surveys/${s._id}`, authHeaders());
            setOpenSurvey(data.survey);
            setResponses(data.responses);
            setCounts(data.counts);
        } catch {
            setError('Failed to load responses.');
        }
    };

    const toggleClass = (id) => {
        setSelectedClasses((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
    };

    const openPreview = async (survey, { classId, followUp = false } = {}) => {
        const classOptions = (survey.tutoringClassIds || []).map((c) => ({
            _id: c._id || c,
            label: c.name
                ? `${c.name} (${c.yearGroup || ''} · ${c.subject || ''})`
                : 'Class',
        }));
        const initialClassId = classId || classOptions[0]?._id || '';
        setPreviewState({
            surveyId: survey._id,
            surveyName: survey.name,
            classOptions,
            classId: initialClassId,
            followUp,
            subject: '',
            html: '',
            classLabel: '',
        });
        await fetchPreview(survey._id, initialClassId, followUp);
    };

    const fetchPreview = async (surveyId, classId, followUp) => {
        setPreviewBusy(true);
        try {
            const params = new URLSearchParams();
            if (classId) params.set('classId', classId);
            if (followUp) params.set('followUp', '1');
            const { data } = await axios.get(
                `${API_URL}/api/admin/renewal-surveys/${surveyId}/preview?${params.toString()}`,
                authHeaders()
            );
            setPreviewState((s) => (s ? { ...s, ...data, classId, followUp } : s));
        } catch (err) {
            setError(err?.response?.data?.message || 'Preview failed.');
        } finally {
            setPreviewBusy(false);
        }
    };

    const openIntent = async (label = '') => {
        setIntentOpen(true);
        setIntentLabel(label);
        setIntentFilter('all');
        await loadIntent(label);
    };

    const loadIntent = async (label) => {
        setIntentBusy(true);
        try {
            const q = label ? `?academicYearLabel=${encodeURIComponent(label)}` : '';
            const { data } = await axios.get(`${API_URL}/api/admin/renewal-surveys/intent${q}`, authHeaders());
            setIntentData(data);
            if (!label && data.label) setIntentLabel(data.label);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load.');
        } finally {
            setIntentBusy(false);
        }
    };

    const intentRows = (intentData?.rows || []).filter((r) => {
        if (intentFilter === 'all') return true;
        if (intentFilter === 'unanswered') return !r.response;
        return r.response === intentFilter;
    });

    const intentGroups = (() => {
        const map = new Map();
        for (const r of intentRows) {
            const key = intentGroupBy === 'class'
                ? r.currentClass || '— No class —'
                : `${r.yearGroup || '—'} · ${r.subject || '—'}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        }
        return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
    })();

    const exportIntentCsv = () => {
        const header = [
            'Parent', 'Parent email', 'Parent phone', 'Child', 'Year group', 'Subject',
            'Current class', 'Response', 'Comments', 'Replied', 'Survey', 'Academic year',
        ];
        const escape = (s) => {
            const v = String(s ?? '');
            return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
        };
        const lines = [header.join(',')];
        for (const r of intentRows) {
            lines.push([
                r.parentName, r.parentEmail, r.parentPhone, r.childName, r.yearGroup,
                r.subject, r.currentClass, r.response || 'unanswered', r.comments,
                r.respondedAt ? new Date(r.respondedAt).toISOString().slice(0, 10) : '',
                r.surveyName, r.academicYearLabel,
            ].map(escape).join(','));
        }
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `renewal-intent-${intentLabel || 'all'}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Renewal surveys</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
                        Ask parents whether they want to continue next academic year. Each parent
                        gets a unique magic-link email — when they click "Reply" they land on a
                        no-login page and pick <em>Yes / No / Maybe</em> with an optional comment.
                        That's what fills the Responses dialog (parents don't reply to the email
                        itself, and no portal login is required). Use <strong>Renewal intent</strong>{' '}
                        to see everyone's answers across all surveys for a given year so you can
                        plan next year's classes.
                    </Typography>
                    
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button onClick={() => openIntent('')} variant="outlined" startIcon={<InsightsIcon />}>
                        Renewal intent
                    </Button>
                    <Button onClick={openCreate} variant="contained" sx={{ color: '#fff !important' }}>
                        New survey
                    </Button>
                </Stack>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Click <strong>New renewal survey</strong>, give it a name (e.g. "2027/28 renewal"), and tick the tutoring classes to include.<br />
                            <strong>2.</strong> Set a default message and, if you want, a bespoke message for individual classes (e.g. "Miss Jones is leaving — options here").<br />
                            <strong>3.</strong> Use <strong>Preview</strong> to see exactly what the parent will receive.<br />
                            <strong>4.</strong> Click <strong>Send</strong>. Every parent of every enrolled student in the chosen classes gets a magic-link email — no login required for them to answer.<br />
                            <strong>5.</strong> Track responses as they come in (Yes / No / Maybe + optional comments). Click <strong>Send follow-up</strong> to nudge only the parents who haven't answered yet.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Year</TableCell>
                                <TableCell>Classes</TableCell>
                                <TableCell>Sent</TableCell>
                                <TableCell>Recipients</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {surveys.map((s) => (
                                <TableRow key={s._id}>
                                    <TableCell>{s.name}</TableCell>
                                    <TableCell>{s.academicYearLabel || '—'}</TableCell>
                                    <TableCell>{s.tutoringClassIds?.length || 0}</TableCell>
                                    <TableCell>{fmt(s.sentAt)}</TableCell>
                                    <TableCell>{s.recipientCount || 0}</TableCell>
                                    <TableCell align="right">
                                        {s.sentAt
                                            ? <>
                                                <Button size="small" onClick={() => openResponses(s)}>Responses</Button>
                                                <Button size="small" startIcon={<VisibilityIcon />} onClick={() => openPreview(s)}>Preview</Button>
                                                <Button size="small" onClick={() => followUp(s._id)}>Follow up</Button>
                                              </>
                                            : <>
                                                <Button size="small" onClick={() => openEdit(s)}>Edit</Button>
                                                <Button size="small" startIcon={<VisibilityIcon />} onClick={() => openPreview(s)}>Preview</Button>
                                                <Button size="small" onClick={() => send(s._id)}>Send</Button>
                                                <IconButton onClick={() => remove(s._id)}><DeleteIcon /></IconButton>
                                              </>}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {surveys.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No surveys yet — click "New survey".
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>

            {/* Create/edit dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editing ? 'Edit survey' : 'New renewal survey'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Survey name" size="small" value={name} onChange={(e) => setName(e.target.value)} />
                        <TextField label="Academic year (label)" placeholder="2027/28" size="small" value={academicYearLabel} onChange={(e) => setAcademicYearLabel(e.target.value)} />
                        <TextField
                            label="Default email body (HTML allowed)"
                            multiline minRows={4}
                            value={defaultMessage}
                            onChange={(e) => setDefaultMessage(e.target.value)}
                            placeholder="e.g. We're starting to plan next year. Please let me know whether you'd like your child to continue."
                        />
                        <Divider />
                        <Typography variant="subtitle2">Target classes</Typography>
                        <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                            {classes.map((c) => (
                                <Box key={c._id} sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={selectedClasses.includes(c._id)} onChange={() => toggleClass(c._id)} />}
                                        label={`${c.name} (${c.yearGroup || ''} · ${c.subject || ''})`}
                                    />
                                    {selectedClasses.includes(c._id) && (
                                        <TextField
                                            placeholder="Optional per-class override message"
                                            value={perClassOverrides[c._id] || ''}
                                            onChange={(e) => setPerClassOverrides((o) => ({ ...o, [c._id]: e.target.value }))}
                                            multiline minRows={2}
                                            size="small"
                                            sx={{ ml: 4 }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    {editing && (
                        <Button
                            startIcon={<VisibilityIcon />}
                            onClick={async () => {
                                await save();
                                openPreview({
                                    _id: editing._id,
                                    name: editing.name,
                                    tutoringClassIds: classes.filter((c) => selectedClasses.includes(c._id)),
                                });
                            }}
                        >
                            Save & preview email
                        </Button>
                    )}
                    <Button onClick={save} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Email preview dialog */}
            <Dialog open={!!previewState} onClose={() => setPreviewState(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Email preview — {previewState?.surveyName}
                </DialogTitle>
                <DialogContent dividers>
                    {previewState && (
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1}>
                                <TextField
                                    select size="small" label="Class"
                                    value={previewState.classId}
                                    onChange={(e) => fetchPreview(previewState.surveyId, e.target.value, previewState.followUp)}
                                    sx={{ minWidth: 280 }}
                                    disabled={!previewState.classOptions?.length}
                                >
                                    {(previewState.classOptions || []).map((c) => (
                                        <MenuItem key={c._id} value={c._id}>{c.label}</MenuItem>
                                    ))}
                                    {!previewState.classOptions?.length && (
                                        <MenuItem value="">No classes on this survey</MenuItem>
                                    )}
                                </TextField>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={previewState.followUp}
                                            onChange={(e) => fetchPreview(previewState.surveyId, previewState.classId, e.target.checked)}
                                        />
                                    }
                                    label="Show follow-up reminder version"
                                />
                            </Stack>
                            <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Subject</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {previewBusy ? 'Loading…' : previewState.subject}
                                </Typography>
                            </Box>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minHeight: 360 }}>
                                <iframe
                                    title="Renewal email preview"
                                    sandbox=""
                                    style={{ width: '100%', height: 420, border: 0, background: '#fff' }}
                                    srcDoc={`<html><head><meta charset="utf-8"><style>body{font-family:Arial,sans-serif;padding:24px;color:#222;line-height:1.5;font-size:14px;max-width:640px}a{pointer-events:none}</style></head><body>${previewState.html || ''}</body></html>`}
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                The button link is a placeholder — each real recipient gets a unique tokenised URL.
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewState(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Responses dialog */}
            <Dialog open={!!openSurvey} onClose={() => setOpenSurvey(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Responses — {openSurvey?.name}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip label={`Yes: ${counts.yes || 0}`} color="success" />
                        <Chip label={`No: ${counts.no || 0}`} color="error" />
                        <Chip label={`Maybe: ${counts.maybe || 0}`} color="warning" />
                        <Chip label={`Unanswered: ${counts.unanswered || 0}`} />
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Parent</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Response</TableCell>
                                <TableCell>Comments</TableCell>
                                <TableCell>Replied</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {responses.map((r) => (
                                <TableRow key={r._id}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {r.parentId?.firstName} {r.parentId?.surname}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{r.parentId?.email}</Typography>
                                    </TableCell>
                                    <TableCell>{r.tutoringClassId?.name || '—'}</TableCell>
                                    <TableCell>
                                        {r.response
                                            ? <Chip size="small" label={r.response} color={RESP_COLOR[r.response]} />
                                            : <Chip size="small" label="—" />}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{r.comments || '—'}</TableCell>
                                    <TableCell>{fmt(r.respondedAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSurvey(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Renewal intent dialog */}
            <Dialog open={intentOpen} onClose={() => setIntentOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Renewal intent</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Aggregated yes / no / maybe answers across every survey for an
                        academic year. Use this list to plan next year's classes — group
                        by current class or by year-group × subject, then export to CSV.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
                        <TextField
                            label="Academic year"
                            size="small"
                            value={intentLabel}
                            onChange={(e) => setIntentLabel(e.target.value)}
                            onBlur={() => loadIntent(intentLabel)}
                            onKeyDown={(e) => { if (e.key === 'Enter') loadIntent(intentLabel); }}
                            placeholder="e.g. 2027/28"
                            sx={{ minWidth: 180 }}
                        />
                        <TextField
                            select size="small" label="Group by"
                            value={intentGroupBy}
                            onChange={(e) => setIntentGroupBy(e.target.value)}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="class">Current class</MenuItem>
                            <MenuItem value="yearSubject">Year group × subject</MenuItem>
                        </TextField>
                        <TextField
                            select size="small" label="Show"
                            value={intentFilter}
                            onChange={(e) => setIntentFilter(e.target.value)}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="yes">Yes only</MenuItem>
                            <MenuItem value="maybe">Maybe only</MenuItem>
                            <MenuItem value="no">No only</MenuItem>
                            <MenuItem value="unanswered">Unanswered</MenuItem>
                        </TextField>
                        <Button onClick={exportIntentCsv} variant="outlined" disabled={!intentRows.length}>
                            Export CSV
                        </Button>
                    </Stack>

                    {intentBusy ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                    ) : (
                        <>
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <Chip label={`Yes: ${intentData?.totals?.yes || 0}`} color="success" />
                                <Chip label={`No: ${intentData?.totals?.no || 0}`} color="error" />
                                <Chip label={`Maybe: ${intentData?.totals?.maybe || 0}`} color="warning" />
                                <Chip label={`Unanswered: ${intentData?.totals?.unanswered || 0}`} />
                            </Stack>

                            {intentGroups.length === 0 ? (
                                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                                    No matching responses yet.
                                </Typography>
                            ) : (
                                intentGroups.map(([groupLabel, rows]) => (
                                    <Box key={groupLabel} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                                            {groupLabel} ({rows.length})
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Parent</TableCell>
                                                    <TableCell>Child</TableCell>
                                                    <TableCell>Year / Subject</TableCell>
                                                    <TableCell>Response</TableCell>
                                                    <TableCell>Comments</TableCell>
                                                    <TableCell>Replied</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.map((r) => (
                                                    <TableRow key={r.responseId}>
                                                        <TableCell>
                                                            <Typography variant="body2">{r.parentName || r.parentEmail}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{r.parentEmail}</Typography>
                                                        </TableCell>
                                                        <TableCell>{r.childName || '—'}</TableCell>
                                                        <TableCell>{[r.yearGroup, r.subject].filter(Boolean).join(' · ') || '—'}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={r.response || 'unanswered'}
                                                                color={RESP_COLOR[r.response] || 'default'}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{r.comments || '—'}</TableCell>
                                                        <TableCell>{fmt(r.respondedAt)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                ))
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIntentOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Send / Follow-up dialog with optional attachments */}
            <Dialog open={!!sendDialog} onClose={() => setSendDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {sendDialog?.kind === 'send' ? 'Send survey' : 'Send follow-up reminder'}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {sendDialog?.kind === 'send'
                            ? `This sends the survey magic-link to every parent in "${sendDialog?.surveyName}". Each parent gets their own tokenised URL.`
                            : `This re-sends the magic-link to every parent who hasn't answered yet on "${sendDialog?.surveyName}".`}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Attachments (optional)</Typography>
                    <EmailAttachmentsInput
                        value={sendAttachments}
                        onChange={setSendAttachments}
                        onError={setError}
                        folder="renewals"
                        helpText="Each file ≤ 25 MB. Attached to every email."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendDialog(null)} disabled={sendBusy}>Cancel</Button>
                    <Button
                        onClick={submitSendDialog}
                        variant="contained"
                        disabled={sendBusy}
                    >
                        {sendBusy ? 'Sending…' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}